import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent } from "../ui/dialog";
import { SubmitHandler, useForm } from "react-hook-form";
import { useRecoilState } from "recoil";
import {
  brokerConnectModalAtom,
  guideModalAtom,
  technicalSupportModalAtom,
} from "@/Atoms/modal/DialogueAtoms";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { useUserContext } from "@/contexts/userContext";
import { useToastHelper } from "@/helpers/helperFunctions";
import { useTranslation } from "react-i18next";
import {
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { Tabs, TabsContent } from "../ui/tabs";

import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import Link from "next/link";
import Loader from "../Loader";
import {
  usePostRevokeBrokerKeys,
  usePostSubmitBrokerKeysV2,
} from "@/api/brokerApi";
import { BROKER_LOGO } from "@/config/logos";
import TrafficSignalPill from "./TrafficSignalPill";
import { Badge } from "../ui/badge";
import { predictionMarketBrokersList } from "../blocks/connection/BrokerList";
import { capitalizeFirst, cn, formatCurrency } from "@/lib/utils";
import { Separator } from "../ui/separator";
import { FaCheckCircle, FaRegCheckCircle } from "react-icons/fa";
import { useRouter } from "next/router";
import RevokeModal from "./RevokeModal";
import { useAppDetails } from "@/contexts/AppDetailsContext";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { useQueryClient } from "@tanstack/react-query";

interface IFormValues {
  apiKey?: string;
  apiSecret?: string;
  passphrase?: string;
}

function PredictionMarketBrokerConnectModal() {
  const connectForm = useForm<IFormValues>({
    mode: "onSubmit",
  });
  const [showConnectForm, setShowConnectForm] = useRecoilState(
    brokerConnectModalAtom,
  );
  const [revokeOpen, setRevokeOpen] = useState<boolean>(false);
  const {
    user: userInfo,
    connectedBrokers,
    refreshConnectedBrokers,
    balanceLoading,
  } = useUserContext();
  const queryClient = useQueryClient();

  useEffect(() => {
    refreshConnectedBrokers();
  }, []);

  const [SCTab, setSCTab] = useState<any>(
    showConnectForm?.brokerId || "27", // Kalshi is ID "27"
  );
  const router = useRouter();
  const { toast } = useToastHelper();
  const { t } = useTranslation("broker-page");

  const {
    mutateAsync: revokeBrokerKeys,
    isPending: isRevokeLoading,
    isSuccess: isRevokeSuccess,
    isError: isRevokeError,
    error: revokeError,
  } = usePostRevokeBrokerKeys();

  const {
    mutateAsync: submitBrokerKeys,
    isPending: isSubmitLoading,
    data: submitData,
    isSuccess: isSubmitSuccess,
  } = usePostSubmitBrokerKeysV2();

  const { brokerConnected } = useMemo(() => {
    const connectedBroker = connectedBrokers?.[SCTab];
    return {
      brokerConnected: !!connectedBroker,
      connectedBroker: connectedBroker,
    };
  }, [connectedBrokers, SCTab]);

  const isSaving = isSubmitLoading || isRevokeLoading;
  const { userType } = useSubscription();
  const [searchTerm, setSearchTerm] = useState("");

  const selectedBroker = useMemo(
    () => predictionMarketBrokersList.find((b) => b.id === SCTab),
    [SCTab],
  );

  const formValues = connectForm.watch();

  const isFormValid = useMemo(() => {
    if (!selectedBroker) return true;

    try {
      const isApiKeyRequired = selectedBroker.defaultApiKey === undefined;
      const isApiSecretRequired = selectedBroker.defaultApiSecret === undefined;
      const isPassphraseRequired =
        selectedBroker.defaultPassphrase === undefined;

      if (isApiKeyRequired && !formValues.apiKey) return false;
      if (isApiSecretRequired && !formValues.apiSecret) return false;
      if (isPassphraseRequired && !formValues.passphrase) return false;

      return true;
    } catch (e) {
      return true;
    }
  }, [selectedBroker, formValues, SCTab]);

  const handleRevokeBrokerKeys = async () => {
    await revokeBrokerKeys({
      userId: userInfo?.user_id,
      key: String(SCTab),
    });
  };

  const handleSubmitBrokerKeys: SubmitHandler<IFormValues> = async (
    values,
    event,
  ) => {
    event?.preventDefault();
    const { apiKey, apiSecret } = values as Required<IFormValues>;
    const rawSecret =
      predictionMarketBrokersList.find((b) => b.id == SCTab)
        ?.defaultApiSecret ?? apiSecret;
    const normalizedSecret = rawSecret ? rawSecret.replace(/\\n/g, "\n") : "";
    try {
      await submitBrokerKeys({
        userId: userInfo?.user_id,
        key:
          predictionMarketBrokersList.find((b) => b.id == SCTab)
            ?.defaultApiKey ?? apiKey,
        secret: normalizedSecret,
        brokerId: Number(SCTab),
        passphrase: "",
      });
    } catch (error: any) {
      toast({ title: error?.response?.data?.detail, variant: "destructive" });
    }

    queryClient.invalidateQueries({
      queryKey: ["get_live_deployments_aggregated_stats", userInfo?.user_id],
    });
  };

  useEffect(() => {
    connectForm.clearErrors();
    connectForm.reset();
  }, [SCTab]);

  useEffect(() => {
    if (isRevokeSuccess) {
      toast({ title: "Broker keys revoked successfully" });
      refreshConnectedBrokers();
      setShowConnectForm({ isOpen: false });
    } else if (isRevokeError && revokeError) {
      const errMessage = revokeError?.response?.data?.detail;
      toast({
        title: errMessage || t("errorWhileDisconnectingTheBroker"),
        variant: "destructive",
      });
    }
  }, [isRevokeLoading, isRevokeSuccess, isRevokeError]);

  useEffect(() => {
    if (isSubmitSuccess) {
      toast({
        title: submitData?.data?.message || "Connected successfully",
      });
      if (submitData?.data?.redirect_url) {
        router.push(submitData?.data?.redirect_url);
      }
      connectForm.setValue("apiKey", "");
      connectForm.setValue("apiSecret", "");
      connectForm.setValue("passphrase", "");
      refreshConnectedBrokers();
      setShowConnectForm({ isOpen: false });
    }
  }, [isSubmitLoading, isSubmitSuccess]);

  const renderInputField = (
    name: "passphrase" | "apiKey" | "apiSecret",
    label: string,
    placeholder: string,
    disabled = false,
    brokerConnectedString?: string,
  ) => (
    <FormField
      control={connectForm.control}
      name={name}
      rules={{ required: name !== "passphrase" && !disabled }}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            {name === "apiSecret" ? (
              <Textarea
                disabled={disabled}
                placeholder={placeholder}
                {...field}
                rows={5}
                className="font-mono text-xs"
                value={
                  disabled && brokerConnectedString
                    ? brokerConnectedString
                    : field.value || ""
                }
              />
            ) : (
              <Input
                autoComplete="off"
                disabled={disabled}
                placeholder={placeholder}
                {...field}
                value={
                  disabled && brokerConnectedString
                    ? brokerConnectedString
                    : field.value || ""
                }
              />
            )}
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );

  return (
    <>
      <Dialog
        open={true}
        onOpenChange={() => {
          setShowConnectForm({ isOpen: false });
          connectForm.clearErrors();
          connectForm.reset();
        }}
      >
        <DialogContent className="max-w-5xl py-0 sm:h-auto h-[100dvh] max-h-[95dvh] overflow-y-scroll overflow-x-hidden px-8">
          <Form {...connectForm}>
            <form
              className="h-full xl:flex xl:flex-col w-full"
              method="post"
              onSubmit={
                brokerConnected
                  ? connectForm.handleSubmit(() => setRevokeOpen(true))
                  : connectForm.handleSubmit(handleSubmitBrokerKeys)
              }
              id="strategy-form"
            >
              <CardHeader className="px-0 w-full flex flex-col gap-0">
                <CardTitle className="flex justify-between pr-4">
                  <div className="flex justify-between w-full flex-col md:flex-row">
                    <div className="text-lg md:text-2xl whitespace-nowrap font-bold tracking-tight">
                      {t("Connect Exchange Account")}
                    </div>
                    <div className="flex text-xs font-bold text-muted-foreground justify-between gap-0">
                      <TrafficSignalPill
                        text={`${
                          Object.keys(connectedBrokers)?.filter((k) =>
                            predictionMarketBrokersList.some((b) => b.id === k),
                          )?.length || 0
                        } ${t("Connected")}`}
                        stoppedText={t("No Broker Connected")}
                        className="text-muted-foreground font-bold"
                      />
                    </div>
                  </div>
                </CardTitle>
              </CardHeader>

              <CardContent className="flex p-0 gap-8 min-h-[480px] flex-col md:flex-row">
                <div className="flex flex-col gap-4 mt-0 w-full md:w-2/3 lg:w-1/2 mx-auto max-h-[550px]">
                  <p className="text-sm text-muted-foreground mb-2">
                    {t(
                      "Connect Level2 to Kalshi to place prediction market trades automatically using secure, encrypted API keys you control and can revoke anytime.",
                    )}
                  </p>

                  {/* Search Input */}
                  <div className="relative px-2 pb-2 pr-2">
                    <Input
                      type="text"
                      placeholder={t("Search brokers...")}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full rounded-lg border border-gray-600 bg-transparent px-3 py-2 text-sm text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="absolute right-3 top-2.5 h-5 w-5 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 104.5 4.5a7.5 7.5 0 0012.15 12.15z"
                      />
                    </svg>
                  </div>

                  {/* Broker List */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 overflow-y-auto px-2 pb-2 pr-2 rounded-md scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-transparent relative">
                    {balanceLoading && (
                      <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-background/50 backdrop-blur-sm rounded-md">
                        <Loader />
                        <p className="text-sm font-medium text-muted-foreground animate-pulse mt-4">
                          {t("Fetching connections. Please wait …")}
                        </p>
                      </div>
                    )}
                    {predictionMarketBrokersList
                      .filter((broker) =>
                        broker.name
                          .toLowerCase()
                          .includes(searchTerm.toLowerCase()),
                      )
                      .map((broker) => {
                        const isSelected = SCTab === broker.id;
                        const cBalance =
                          connectedBrokers?.[Number(broker.id)]?.balance;
                        const isDisabled = broker.disabled;

                        return (
                          <div
                            key={broker.id}
                            onClick={() => {
                              if (isDisabled) return;
                              setSCTab(broker.id);
                            }}
                            className={cn(
                              "cursor-pointer rounded-xl border p-5 h-[140px] overflow-hidden text-center transition-all flex flex-col gap-2 items-center justify-center relative shadow-sm",
                              isSelected
                                ? "border-blue-500 bg-blue-500/10"
                                : "border-gray-700 hover:border-gray-500 hover:bg-gray-800/40",
                              isDisabled && "opacity-50 hover:border-gray-700",
                            )}
                          >
                            {broker.logo && (
                              <img
                                src={broker.logo}
                                alt={broker.name}
                                className="mx-auto h-8 object-contain"
                              />
                            )}
                            <div>
                              <p className="text-xs text-muted-foreground">
                                {broker.note}
                              </p>
                              {cBalance && (
                                <div className="flex bottom-0 justify-center w-full h-8 flex-col items-center gap-1">
                                  <div className="flex gap-1 items-center">
                                    <p
                                      className={cn(
                                        "font-semibold text-xs text-gray-300",
                                      )}
                                    >
                                      {formatCurrency(
                                        cBalance,
                                        2,
                                        false,
                                        undefined,
                                        true,
                                      )}
                                    </p>
                                  </div>
                                </div>
                              )}
                            </div>

                            {cBalance && (
                              <div className="flex absolute bottom-0 justify-center w-full h-6 bg-[--marketplace-green] items-center gap-1">
                                <FaCheckCircle className="text-white text-xs" />
                                <p className="text-xs font-bold">
                                  {t("Connected")}
                                </p>
                              </div>
                            )}
                          </div>
                        );
                      })}
                  </div>
                </div>

                <Separator orientation="vertical" />
                <Tabs value={SCTab} className="md:w-1/2">
                  <TabsContent value={"27"}>
                    <div className="flex flex-col gap-2 mt-0 px-4">
                      <div className="flex flex-col gap-6 pt-4">
                        <img
                          src={BROKER_LOGO.KALSHI.DARK.textLogo}
                          alt={"kalshi-logo"}
                          className="mx-auto h-8 mb-2 object-contain"
                        />

                        <div>
                          <CardTitle className="text-base text-muted-foreground">
                            {t("1. Open a Kalshi account")}
                          </CardTitle>
                          <CardDescription>
                            <a
                              href={"https://kalshi.com/"}
                              className="underline"
                              target="_blank"
                              rel="noreferrer"
                            >
                              {t("Click here")}
                            </a>{" "}
                            {t("to open a free account with Kalshi")}
                          </CardDescription>
                        </div>
                        <div>
                          <CardTitle className="text-base text-muted-foreground">
                            {t("2. Connect to the API")}
                          </CardTitle>
                          <CardDescription>
                            {t(
                              "Enter your API credentials below to enable fully automated trading",
                            )}
                          </CardDescription>
                        </div>
                      </div>

                      <div className="flex flex-col gap-2 mt-4">
                        {renderInputField(
                          "apiKey",
                          t("API Key ID"),
                          t("API Key is Required"),
                          brokerConnected,
                          "********",
                        )}
                        {renderInputField(
                          "apiSecret",
                          t("Private Key"),
                          t("API secret is required"),
                          brokerConnected,
                          "********",
                        )}
                      </div>
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>

              {SCTab != "" && (
                <CardFooter className="grow px-0 gap-4 flex-col mt-10 md:mt-auto min-h-[105px]">
                  <div className="flex w-full h-full">
                    <div className="flex flex-col w-full float-right h-full">
                      <div className="flex flex-col w-full lg:flex-row justify-end">
                        <div className="w-full flex items-center justify-end">
                          <Button
                            disabled={
                              isSaving || (!brokerConnected && !isFormValid)
                            }
                            loadingText={isSaving}
                            variant={
                              brokerConnected ? "destructive" : "default"
                            }
                            className={cn("text-sm gap-2 flex items-center")}
                          >
                            {isSaving ? (
                              <>
                                {brokerConnected
                                  ? t("Revoking")
                                  : t("Connecting")}
                              </>
                            ) : (
                              <>
                                {brokerConnected ? t("Revoke") : t("Connect")}
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardFooter>
              )}
            </form>
          </Form>
        </DialogContent>
      </Dialog>
      {revokeOpen && (
        <RevokeModal
          isOpen={revokeOpen}
          onClose={() => setRevokeOpen(false)}
          onConfirm={handleRevokeBrokerKeys}
        />
      )}
    </>
  );
}

export default PredictionMarketBrokerConnectModal;
